import React, { ReactNode } from "react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { fadeIn } from "styles/animations";
import { Text } from "styles/utility-components";

import { FeatureType } from "components/paywall/paywallUtils";
import BetaIcon, { BetaFeatureType } from "components/ui/beta_icon/BetaIcon";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import UIcon, { IconProps } from "lib/icons/untitled_ui/UIcon";
import { OpenPaywallModalOptions, usePaywall } from "lib/providers/Paywall";
import { isRouteActive } from "./utils";

type NavItemProps = {
  title: string;
  icon?: IconProps["icon"];
  expanded?: boolean;
  path?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => void;
  children?: ReactNode;
  isSubNavExpanded?: boolean;
  paywall?: FeatureType;
  paywallOptions?: OpenPaywallModalOptions;
  badge?: string | number;
  shouldAnimate?: boolean;
  unreadNotifications?: number;
  betaFeature?: BetaFeatureType;
  isActive?: boolean;
};

function NavItem({
  title,
  icon,
  expanded = true,
  path,
  onClick,
  children,
  isSubNavExpanded = false,
  paywall,
  paywallOptions,
  unreadNotifications,
  shouldAnimate = false,
  betaFeature = undefined,
  isActive = false,
}: NavItemProps) {
  const { pathname } = useLocation();
  const active = isRouteActive(path, expanded, isSubNavExpanded, isActive, pathname);

  const { openPaywallModal } = usePaywall();
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>) => {
    if (paywall) {
      openPaywallModal(paywall, paywallOptions);
      return;
    }
    // SPA-navigate internal links on a plain left-click; let modifier/middle clicks
    // and the real href handle open-in-new-tab and accessibility.
    if (path && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      void navigate({ to: path });
    }
    onClick?.(e);
  };

  return (
    <Wrapper>
      <ItemWrapper
        as={path && !paywall ? "a" : "button"}
        onClick={handleClick}
        tabIndex={0}
        href={path && !paywall ? path : undefined}
        active={!!active}
        data-has-children={!!children}
        data-is-subnav-expanded={isSubNavExpanded && expanded}
      >
        {icon && (
          <IconWrapper>
            {unreadNotifications && <RedDot />}
            <UIcon icon={icon} className="icon" />
            {children && <UIcon icon="chevronRight" className="iconChevron" />}
          </IconWrapper>
        )}
        {expanded && (
          <EllipsisTooltip fullText={title} tooltipProps={{ side: "right" }}>
            {(ref) => (
              <StyledText
                ref={ref}
                fontWeight="500"
                color="inherit"
                shouldAnimate={shouldAnimate}
                ellipsis
              >
                {title}
                {betaFeature && (
                  <>
                    {" "}
                    <BetaIcon betaFeature={betaFeature} />
                  </>
                )}
              </StyledText>
            )}
          </EllipsisTooltip>
        )}
        {paywall && expanded && <UIcon icon={"lock01"} size={14} />}
        {unreadNotifications && expanded && <Badge>{unreadNotifications}</Badge>}
      </ItemWrapper>
      {children}
    </Wrapper>
  );
}

export default NavItem;

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
});

const ItemWrapper = styled.a<{ active: boolean }>(({ theme, active }) => ({
  display: "flex",
  flexWrap: "nowrap",
  gap: 8,
  height: 32,
  minWidth: 32,
  alignItems: "center",
  padding: 8,
  borderRadius: 6,
  color: theme.colors.sysTextDefault,
  backgroundColor: "transparent",
  border: "none",
  transition: "all 0.3s",
  userSelect: "none",

  "&:hover, &:focus-visible": {
    cursor: "pointer",
    color: theme.colors.sysTextWhite,
    outline: "none",

    ...(!active && {
      backgroundColor: theme.colors.sysPrimarySubtle,
      color: theme.colors.sysPrimaryHover,
    }),
  },

  ...(active && {
    backgroundColor: theme.colors.sysPrimaryDefault,
    color: theme.colors.sysTextWhite,
  }),

  ".icon": {
    transition: "opacity 0.4s",
    opacity: 1,
  },
  ".iconChevron": {
    position: "absolute",
    opacity: 0,
    transform: "rotate(0deg)",
    transition: "opacity 0.4s ease, transform 0.3s",
    transitionDelay: "0s, 0s",
  },
  "&[data-is-subnav-expanded='true'] .iconChevron": {
    transform: "rotate(90deg)",
  },
  "&[data-has-children='true']": {
    ".icon": {
      transitionDelay: "0.15s",
    },
    "&:hover, &:focus-visible": {
      ".icon": {
        opacity: 0,
        transitionDelay: "0s",
      },
      ".iconChevron": {
        opacity: 1,
        transitionDelay: "0.15s, 0s",
      },
    },
  },
}));

const StyledText = styled(Text)<{ shouldAnimate: boolean }>(({ shouldAnimate }) => ({
  ...(shouldAnimate && {
    opacity: 0,
    animation: `${fadeIn} 0.3s forwards`,
  }),
}));

const IconWrapper = styled.div({
  position: "relative",
  display: "flex",
  alignItems: "center",
});

const Badge = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: 24,
  width: 24,
  borderRadius: 4,
  backgroundColor: theme.colors.sysPrimarySubtle,
  color: theme.colors.sysPrimaryDefault,
  fontSize: 12,
  fontWeight: 500,
  marginLeft: "auto",
}));

const RedDot = styled.div(({ theme }) => ({
  position: "absolute",
  top: -2,
  right: 1,
  height: 8,
  width: 8,
  border: `1px solid ${theme.colors.sysBackgroundDefault}`,
  borderRadius: "50%",
  backgroundColor: theme.colors.sysDestructiveDefault,
  animation: `${fadeIn} 1s forwards`,
}));
